/**
 * HomeServicesGrid.jsx — Grille 3 colonnes des services phares.
 *
 * Affichée juste sous le hero sur la Home. Exploite la largeur desktop
 * (1700px) pour mettre en avant les 3 formations phares :
 *  1. Permis AM (14/15 ans)
 *  2. Formation 125 cm³ (7h passerelle B → 125)
 *  3. Annuaire des moto-écoles
 *
 * Chaque carte : icône + titre + accroche + cible + CTA.
 * Design : cartes avec hover subtil, badges NEW cohérents avec la topbar.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Bike, MapPinned, ArrowRight, Sparkles } from 'lucide-react';
import { Badge } from './ui/badge';

const SERVICES = [
  {
    to: '/permis-am',
    icon: Zap,
    kicker: 'Dès 14 ans',
    title: 'Permis AM',
    description:
      "Formation complète pour conduire un scooter ou cyclomoteur 50 cm³. Code théorique + 8 h de pratique.",
    cta: 'Découvrir le permis AM',
    accent: 'from-amber-500/15 to-orange-500/5',
    iconClass: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    testid: 'service-permis-am',
  },
  {
    to: '/formation-125',
    icon: Bike,
    kicker: 'Permis B + 2 ans',
    title: 'Formation 125 cm³',
    description:
      "Formation 7 heures obligatoire pour conduire une 125 cm³ ou un scooter 3 roues avec le permis B.",
    cta: 'Voir la formation 125',
    accent: 'from-rose-500/15 to-red-500/5',
    iconClass: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
    testid: 'service-formation-125',
  },
  {
    to: '/annuaire',
    icon: MapPinned,
    kicker: 'Partout en France',
    title: 'Annuaire moto-écoles',
    description:
      "Trouvez une moto-école près de chez vous. Avis, formations proposées, géolocalisation en 1 clic.",
    cta: "Explorer l'annuaire",
    accent: 'from-emerald-500/15 to-teal-500/5',
    iconClass: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    testid: 'service-annuaire',
  },
];

export default function HomeServicesGrid() {
  return (
    <section
      className="relative"
      data-testid="home-services-grid"
      aria-label="Services et formations"
    >
      {/* Entête de section */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-black text-[hsl(var(--primary))] mb-1.5">
            <Sparkles className="w-3 h-3" /> Nouveau · 2026
          </div>
          <h2
            className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Trois nouvelles entrées pour les 2 roues
          </h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-md leading-relaxed sm:text-right">
          Que vous visiez le scooter dès 14 ans, la 125 cm³ avec le permis B,
          ou que vous cherchiez votre moto-école : on vous accompagne.
        </p>
      </div>

      {/* Grille 3 colonnes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        {SERVICES.map((s) => {
          const Icon = s.icon;
          return (
            <Link
              key={s.to}
              to={s.to}
              data-testid={s.testid}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card hover:border-[hsl(var(--primary))]/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ease-out flex flex-col"
            >
              {/* Dégradé décoratif discret */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${s.accent} opacity-60 pointer-events-none`}
                aria-hidden="true"
              />

              <div className="relative p-5 lg:p-6 flex flex-col gap-3 flex-1">
                {/* En-tête : icône + badge NEW */}
                <div className="flex items-start justify-between gap-2">
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.iconClass}`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <Badge className="bg-[hsl(var(--primary))] text-white border-none text-[9px] px-1.5 py-0.5 tracking-wider font-black">
                    NEW
                  </Badge>
                </div>

                {/* Kicker + Titre */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] font-black text-muted-foreground mb-1">
                    {s.kicker}
                  </div>
                  <h3 className="text-lg lg:text-xl font-extrabold tracking-tight leading-tight text-foreground">
                    {s.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-sm text-foreground/70 leading-relaxed flex-1">
                  {s.description}
                </p>

                {/* CTA */}
                <div className="pt-1 inline-flex items-center gap-1.5 text-sm font-bold text-[hsl(var(--primary))] group-hover:gap-2.5 transition-all">
                  {s.cta}
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
